import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';

export interface PageBreadcrumbSegment {
  title: React.ReactNode;
  path?: string;
}

interface PageBreadcrumbContextValue {
  segments: PageBreadcrumbSegment[] | null;
  setPageBreadcrumb: (segments: PageBreadcrumbSegment[] | null) => void;
}

const PageBreadcrumbContext = createContext<PageBreadcrumbContextValue | undefined>(undefined);

export const PageBreadcrumbProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [segments, setSegments] = useState<PageBreadcrumbSegment[] | null>(null);

  const setPageBreadcrumb = useCallback((s: PageBreadcrumbSegment[] | null) => {
    setSegments(s);
  }, []);

  const value = useMemo(
    () => ({ segments, setPageBreadcrumb }),
    [segments, setPageBreadcrumb]
  );

  return <PageBreadcrumbContext.Provider value={value}>{children}</PageBreadcrumbContext.Provider>;
};

export function usePageBreadcrumb() {
  const ctx = useContext(PageBreadcrumbContext);
  if (!ctx) {
    throw new Error('usePageBreadcrumb must be used within PageBreadcrumbProvider');
  }
  return ctx;
}

function breadcrumbSegmentsKey(segments: PageBreadcrumbSegment[] | null): string {
  if (segments == null) return '';
  return JSON.stringify(
    segments.map((s) => ({
      title: typeof s.title === 'string' ? s.title : String(s.title),
      path: s.path ?? '',
    }))
  );
}

/** For detail pages: set breadcrumb on load, clear on unmount */
export function useDocumentBreadcrumb(segments: PageBreadcrumbSegment[] | null) {
  const { setPageBreadcrumb } = usePageBreadcrumb();
  const segmentsRef = React.useRef(segments);
  segmentsRef.current = segments;
  const segmentsKey = breadcrumbSegmentsKey(segments);

  React.useEffect(() => {
    setPageBreadcrumb(segmentsRef.current);
    return () => setPageBreadcrumb(null);
  }, [segmentsKey, setPageBreadcrumb]);
}
